// C-1: sectioned grid layout — the two browse sections.
//
// Streams are grouped by `status` into SECTIONS ("Live now" / "Starting
// soon"); live and upcoming never interleave in one grid. A section with
// zero streams hides entirely, including its header; when both are empty
// a single centered message renders instead.
//
// Only the SECTION HEADER dot pulses (never the card badges), and the
// pulse is disabled under prefers-reduced-motion via motion-reduce.
//
// All colors come from the browse-* tokens (raw hex fails the designTokens
// jest check).
import React from 'react';
import posthog from 'posthog-js';
import StreamCard from './StreamCard';
import StreamCardBody from './StreamCardBody';
import LiveBadges from './LiveBadges';
import LiveThumbnail from './LiveThumbnail';
import UpcomingCard from './UpcomingCard';
import SkeletonCard from './SkeletonCard';
import StatsFlip from './StatsFlip';

// "Starting soon" covers the next 12 hours (its subtitle says so);
// everything further out lands in "Coming up" with dated cards, so
// far-future streams (D23 etc.) stay discoverable. Past-due upcoming
// streams DROP immediately — a card promising a start time in the past
// reads as broken; the stream reappears under Live once a refresh reports
// it live. Unknown start times stay in Starting soon.
export const UPCOMING_WINDOW_MS = 12 * 60 * 60 * 1000;

// 4-up at 1440; 3-up <1200, 2-up <900, 1-up <620 — these steps keep every
// card ≥260px wide inside the 1440px frame.
// Standard Tailwind breakpoints (640/768/1280) — the design's suggested
// 620/900/1200 steps used min-[...] arbitrary variants, which this
// Tailwind setup silently drops from the emitted CSS.
const GRID_CLS =
  'grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4';

// SOR-625: upcoming sections render as compact ROWS below sm (UpcomingCard
// mobileRow), so the mobile gap tightens to 8px — ~5+ rows fit an 844px
// screen. Arbitrary spellings: Bootstrap's .gap-2{!important} would force
// 8px at ALL widths, and sm:gap-[24px] pins the desktop gap to what the
// deployed grid actually renders (Bootstrap's .gap-4!important forces
// GRID_CLS to 24px — pre-existing, see follow-up) so desktop is unchanged.
const UPCOMING_GRID_CLS =
  'grid gap-[8px] sm:gap-[24px] grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4';

// D-1: eight card-geometry skeletons in the SAME section grid classes the
// real sections use — the loading state swaps 1:1 with a section, so
// nothing reflows on data arrival.
export const BrowseSkeletons = () => (
  <div role="status" aria-label="Loading streams" className={GRID_CLS}>
    {Array.from({ length: 8 }, (_, i) => (
      <SkeletonCard key={i} />
    ))}
  </div>
);

const SectionHeader = ({ dot, title, subtitle }) => (
  <div className="flex items-baseline gap-2.5">
    {dot && (
      <span
        aria-hidden="true"
        className="h-[7px] w-[7px] shrink-0 self-center rounded-full bg-browse-live animate-live-pulse motion-reduce:animate-none"
      />
    )}
    <h2 className="font-inter-tight text-base font-semibold leading-1.2 tracking-section text-browse-text-primary">
      {title}
    </h2>
    <span className="font-inter-tight text-13 font-normal leading-1.2 text-browse-text-tertiary">
      {subtitle}
    </span>
  </div>
);

// SOR-618: live cards flip to session stats; upcoming cards have no session.
const LiveCard = ({ stream }) => (
  <StatsFlip stream={stream}>
    <StreamCard
      watchUrl={stream.watchUrl}
      onClick={() => posthog.capture('stream_clicked', {
        stream_id: stream.id,
        stream_status: 'live',
        viewer_count: stream.viewerCount,
      })}
      thumbnail={
        <>
          <LiveThumbnail stream={stream} />
          <LiveBadges viewerCount={stream.viewerCount} surging={stream.surging} />
        </>
      }
    >
      <StreamCardBody
        title={stream.title}
        channelName={stream.channelName}
        channelAvatarUrl={stream.channelAvatarUrl}
        metaRight={stream.parkCode}
      />
    </StreamCard>
  </StatsFlip>
);

// Rising sort (owner coherence rule): every badged stream ranks above every
// unbadged one — (surging desc, latest_z desc, watchers desc as tiebreak).
// Client-side over the already-fetched live list; the z number itself is
// dashboard vocabulary and never rendered. Badged streams rank first
// (z desc within); everything else keeps Most-watched order — ranking
// non-surging streams by z would order statistical noise (a 24/7 cam's
// idle wobble can out-z a steady real stream), so when nothing is rising,
// Rising deliberately equals Most watched.
const risingSort = (a, b) =>
  (b.surging === true) - (a.surging === true) ||
  (a.surging === true ? (b.latestZ ?? -Infinity) - (a.latestZ ?? -Infinity) : 0) ||
  (b.viewerCount ?? 0) - (a.viewerCount ?? 0);

/** "12 streams · 1,284 watching" — watchers omitted when unreported. */
const liveSubtitle = (streams) => {
  const watchers = streams.reduce((sum, s) => sum + (s.viewerCount || 0), 0);
  const count = `${streams.length} stream${streams.length === 1 ? '' : 's'}`;
  return watchers > 0
    ? `${count} · ${watchers.toLocaleString('en-US')} watching`
    : count;
};

/**
 * @param {Object} props
 * @param {import('./stream').Stream[]} props.streams
 * @param {Set<number>} props.reminders  stream ids with a reminder set
 * @param {(streamId: number) => void} props.onToggleReminder
 * @param {number} [props.now]  epoch ms; pass to pin the clock in tests
 * @param {string} [props.timeZone]  override for tests; defaults to the viewer's zone
 */
const BrowseSections = ({
  streams,
  reminders,
  onToggleReminder,
  sort = 'watched', // owned by the header's Sort dropdown (mobile: FilterSheet)
  now = Date.now(),
  timeZone,
}) => {
  const live = streams
    .filter((s) => s.status === 'live')
    .sort((a, b) => (b.viewerCount ?? 0) - (a.viewerCount ?? 0));
  // surging is null on every stream while the server kill switch is off —
  // no Rising data means 'watched' order regardless of the selected sort.
  const hasRisingData = live.some((s) => s.surging != null);
  if (hasRisingData && sort === 'rising') live.sort(risingSort);
  const upcoming = streams
    .filter(
      (s) =>
        s.status === 'upcoming' &&
        (s.startsAt == null || Date.parse(s.startsAt) > now)
    )
    .sort(
      (a, b) =>
        (a.startsAt ? Date.parse(a.startsAt) : Infinity) -
        (b.startsAt ? Date.parse(b.startsAt) : Infinity)
    );
  const soon = upcoming.filter(
    (s) => s.startsAt == null || Date.parse(s.startsAt) - now <= UPCOMING_WINDOW_MS
  );
  const later = upcoming.filter(
    (s) => s.startsAt != null && Date.parse(s.startsAt) - now > UPCOMING_WINDOW_MS
  );

  if (live.length === 0 && upcoming.length === 0) {
    return (
      <p className="py-8.5 text-center font-inter-tight text-sm text-browse-text-tertiary">
        No streams right now — check back soon.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      {live.length > 0 && (
        <section aria-label="Live now">
          <SectionHeader dot title="Live now" subtitle={liveSubtitle(live)} />
          <div className={`${GRID_CLS} mt-3.5`}>
            {live.map((s) => (
              <LiveCard key={s.id} stream={s} />
            ))}
          </div>
        </section>
      )}
      {soon.length > 0 && (
        <section aria-label="Starting soon">
          <SectionHeader
            title="Starting soon"
            subtitle="next 12 hours, your time"
          />
          <div className={`${UPCOMING_GRID_CLS} mt-3.5`}>
            {soon.map((s) => (
              <UpcomingCard
                key={s.id}
                stream={s}
                reminded={reminders.has(s.id)}
                onToggleReminder={onToggleReminder}
                now={now}
                timeZone={timeZone}
              />
            ))}
          </div>
        </section>
      )}
      {later.length > 0 && (
        <section aria-label="Coming up">
          <SectionHeader
            title="Coming up"
            subtitle="beyond the next 12 hours, your time"
          />
          <div className={`${UPCOMING_GRID_CLS} mt-3.5`}>
            {later.map((s) => (
              <UpcomingCard
                key={s.id}
                stream={s}
                dated
                reminded={reminders.has(s.id)}
                onToggleReminder={onToggleReminder}
                now={now}
                timeZone={timeZone}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default BrowseSections;
